import { UserExperimentSetting } from '../applications/ports/userExperimentRepository'

enum ActionType {
  FETCH = 'userExperiment/FETCH',
  FETCH_SUCCESS = 'userExperiment/FETCH_SUCCESS',
  FETCH_FAILURE = 'userExperiment/FETCH_FAILURE',
  UPDATE = 'userExperiment/UPDATE',
  UPDATE_SUCCESS = 'userExperiment/UPDATE_SUCCESS',
  UPDATE_FAILURE = 'userExperiment/UPDATE_FAILURE',
  ACKNOWLEDGE = 'userExperiment/ACKNOWLEDGE',
  ADD_ACTIVE_EXPERIMENTS = 'userExperiment/ADD_ACTIVE_EXPERIMENTS',
  REMOVE_ACTIVE_EXPERIMENTS = 'userExperiment/REMOVE_ACTIVE_EXPERIMENTS',
}

type State = {
  settings: Record<string, boolean> // { experimentId: useNew }
  isLoaded: boolean
  isLoading: boolean
  acknowledgedExperiments: string[] // バナーを閉じた実験ID
  activeExperiments: string[] // 現在の画面で対象となる実験ID
  error: string | null
}

const initialState: State = {
  settings: {},
  isLoaded: false,
  isLoading: false,
  acknowledgedExperiments: [],
  activeExperiments: [],
  error: null,
}

// Action Creators
export const fetchUserExperiments = () => ({
  type: ActionType.FETCH as const,
})

export const fetchUserExperimentsSuccess = (
  settings: UserExperimentSetting[]
) => ({
  type: ActionType.FETCH_SUCCESS as const,
  payload: settings,
})

export const fetchUserExperimentsFailure = (error: string) => ({
  type: ActionType.FETCH_FAILURE as const,
  payload: error,
})

export const updateUserExperiment = (
  experimentId: string,
  useNew: boolean
) => ({
  type: ActionType.UPDATE as const,
  payload: { experimentId, useNew },
})

export const updateUserExperimentSuccess = () => ({
  type: ActionType.UPDATE_SUCCESS as const,
})

export const updateUserExperimentFailure = (error: string) => ({
  type: ActionType.UPDATE_FAILURE as const,
  payload: error,
})

export const acknowledgeExperiment = (experimentId: string) => ({
  type: ActionType.ACKNOWLEDGE as const,
  payload: experimentId,
})

export const addActiveExperiments = (experimentIds: string[]) => ({
  type: ActionType.ADD_ACTIVE_EXPERIMENTS as const,
  payload: experimentIds,
})

export const removeActiveExperiments = (experimentIds: string[]) => ({
  type: ActionType.REMOVE_ACTIVE_EXPERIMENTS as const,
  payload: experimentIds,
})

type Action =
  | ReturnType<typeof fetchUserExperiments>
  | ReturnType<typeof fetchUserExperimentsSuccess>
  | ReturnType<typeof fetchUserExperimentsFailure>
  | ReturnType<typeof updateUserExperiment>
  | ReturnType<typeof updateUserExperimentSuccess>
  | ReturnType<typeof updateUserExperimentFailure>
  | ReturnType<typeof acknowledgeExperiment>
  | ReturnType<typeof addActiveExperiments>
  | ReturnType<typeof removeActiveExperiments>

// Reducer
export const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case ActionType.FETCH:
      return { ...state, isLoading: true }

    case ActionType.FETCH_SUCCESS: {
      const newSettings: Record<string, boolean> = {}
      action.payload.forEach(setting => {
        newSettings[setting.experimentId] = setting.useNew
      })
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        settings: newSettings,
        // 設定済みの実験はacknowledged扱い
        acknowledgedExperiments: action.payload.map(s => s.experimentId),
      }
    }

    case ActionType.FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload,
      }

    case ActionType.UPDATE: {
      const { experimentId, useNew } = action.payload
      const newAcknowledged = state.acknowledgedExperiments.includes(
        experimentId
      )
        ? state.acknowledgedExperiments
        : [...state.acknowledgedExperiments, experimentId]
      return {
        ...state,
        settings: { ...state.settings, [experimentId]: useNew },
        acknowledgedExperiments: newAcknowledged,
      }
    }

    case ActionType.UPDATE_SUCCESS:
      return state // Optimistic updateのため特に処理なし

    case ActionType.UPDATE_FAILURE:
      return { ...state, error: action.payload }

    case ActionType.ACKNOWLEDGE: {
      const experimentId = action.payload
      if (state.acknowledgedExperiments.includes(experimentId)) {
        return state
      }
      return {
        ...state,
        acknowledgedExperiments: [
          ...state.acknowledgedExperiments,
          experimentId,
        ],
      }
    }

    case ActionType.ADD_ACTIVE_EXPERIMENTS: {
      const newIds = action.payload.filter(
        id => !state.activeExperiments.includes(id)
      )
      if (newIds.length === 0) return state
      return {
        ...state,
        activeExperiments: [...state.activeExperiments, ...newIds],
      }
    }

    case ActionType.REMOVE_ACTIVE_EXPERIMENTS: {
      const idsToRemove = action.payload
      const filtered = state.activeExperiments.filter(
        id => !idsToRemove.includes(id)
      )
      if (filtered.length === state.activeExperiments.length) return state
      return {
        ...state,
        activeExperiments: filtered,
      }
    }

    default:
      return state
  }
}

// Selectors
export const selectExperimentUseNew = (
  state: { userExperiment: State },
  experimentId: string
): boolean => {
  // 設定がない場合は新しいUI（useNew = true）がデフォルト
  return state.userExperiment.settings[experimentId] ?? true
}

export const selectIsExperimentAcknowledged = (
  state: { userExperiment: State },
  experimentId: string
): boolean => {
  return state.userExperiment.acknowledgedExperiments.includes(experimentId)
}

export const selectIsUserExperimentLoaded = (state: {
  userExperiment: State
}): boolean => state.userExperiment.isLoaded

export const selectActiveExperiments = (state: {
  userExperiment: State
}): string[] => state.userExperiment.activeExperiments

export const selectAcknowledgedExperiments = (state: {
  userExperiment: State
}): string[] => state.userExperiment.acknowledgedExperiments
